// src/pages/Products/ProductDetails.js
import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { doc, getDoc, deleteDoc, collection, getDocs } from "firebase/firestore";
import { ref, deleteObject } from "firebase/storage";
import { db, storage } from "../../../db/firebase";

// Markdown Preview Component (same as in ProductForm)
const MarkdownPreview = ({ content }) => {
  const renderMarkdown = (text) => {
    if (!text) return <p className="text-gray-500 italic">No detailed description available.</p>;
    
    // Basic markdown parsing
    const html = text
      // Headers
      .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-xl font-semibold mt-4 mb-2">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-4 mb-3">$1</h1>')
      // Bold
      .replace(/\*\*(.*?)\*\*/gim, '<strong class="font-bold">$1</strong>')
      // Italic
      .replace(/\*(.*?)\*/gim, '<em class="italic">$1</em>')
      // Underline
      .replace(/<u>(.*?)<\/u>/gim, '<u class="underline">$1</u>')
      // Bullet points
      .replace(/^- (.*$)/gim, '<li class="ml-4 list-disc">$1</li>')
      // Numbered lists
      .replace(/^\d+\. (.*$)/gim, '<li class="ml-4 list-decimal">$1</li>')
      // Line breaks
      .replace(/\n$/gim, '<br>')
      // Paragraphs
      .replace(/\n\n/gim, '</p><p class="my-2">')
      .replace(/([^\n])\n([^\n])/gim, '$1<br>$2');
    
    return `<p class="my-2">${html}</p>`;
  };

  return (
    <div 
      className="prose max-w-none p-4 border border-gray-300 rounded-lg bg-white"
      dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
    />
  );
};

export default function ProductDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [categories, setCategories] = useState([]); // NEW: Store all categories
  const [organizedCategories, setOrganizedCategories] = useState([]); // NEW: Organized with hierarchy

  useEffect(() => {
    const getProduct = async () => {
      try {
        setLoading(true);
        const snap = await getDoc(doc(db, "products", id));
        if (snap.exists()) {
          setProduct({ id: snap.id, ...snap.data() });
        }
      } catch (error) {
        console.error("Error fetching product:", error);
      } finally {
        setLoading(false);
      }
    };
    getProduct();
  }, [id]);

  // NEW: Fetch categories and organize them
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const snap = await getDocs(collection(db, "categories"));
        const categoriesData = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setCategories(categoriesData);
        
        // Organize categories into hierarchy
        const organized = organizeCategoriesHierarchy(categoriesData);
        setOrganizedCategories(organized);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    
    fetchCategories();
  }, []);

  // NEW: Function to organize categories into hierarchy
  const organizeCategoriesHierarchy = (categoriesData) => {
    const mainCategories = categoriesData.filter(cat => !cat.ParentCategory);
    const subCategories = categoriesData.filter(cat => cat.ParentCategory);

    // Add subcategories to their parent categories
    const categoriesWithChildren = mainCategories.map(mainCat => ({
      ...mainCat,
      subcategories: subCategories.filter(subCat => subCat.ParentCategory === mainCat.id)
    }));

    return categoriesWithChildren;
  };

  // NEW: Function to get category names with hierarchy
  const getCategoryNamesWithHierarchy = () => {
    if (!product?.PCategories || !organizedCategories.length) return [];

    const selectedCategories = [];
    
    organizedCategories.forEach(mainCat => {
      // Check if main category is selected
      if (product.PCategories.includes(mainCat.id)) {
        selectedCategories.push({
          id: mainCat.id,
          name: mainCat.CategoryName,
          type: 'main',
          hasSubcategories: mainCat.subcategories && mainCat.subcategories.length > 0
        });
      }
      
      // Check subcategories
      if (mainCat.subcategories) {
        mainCat.subcategories.forEach(subCat => {
          if (product.PCategories.includes(subCat.id)) {
            selectedCategories.push({
              id: subCat.id,
              name: subCat.CategoryName,
              type: 'sub',
              parentName: mainCat.CategoryName
            });
          }
        });
      }
    });

    return selectedCategories;
  };

  // Function to delete files from Firebase Storage
  const deleteStorageFiles = async (fileUrls) => {
    if (!fileUrls || !Array.isArray(fileUrls)) return;

    const deletePromises = fileUrls.map(async (fileUrl) => {
      try {
        const matches = fileUrl.match(/o\/(.+?)\?/);
        if (matches && matches[1]) {
          const filePath = decodeURIComponent(matches[1]);
          const fileRef = ref(storage, filePath);
          await deleteObject(fileRef);
        }
      } catch (error) {
        console.error('Error deleting file:', fileUrl, error);
      }
    });

    await Promise.all(deletePromises);
  };

  // Function to delete all media files for a product
  const deleteProductMedia = async (product) => {
    if (!product.PType || !Array.isArray(product.PType)) return;

    const allMediaDeletions = product.PType.map(async (variant) => {
      const imageDeletions = deleteStorageFiles(variant.PImgUrls);
      const videoDeletions = deleteStorageFiles(variant.PVidUrls);
      await Promise.all([imageDeletions, videoDeletions]);
    });

    await Promise.all(allMediaDeletions);
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this product? This will also delete all associated images and videos.")) {
      return;
    }

    try {
      setDeleting(true);
      
      // Delete all media files from Firebase Storage first
      await deleteProductMedia(product);

      // Then delete the product document from Firestore
      await deleteDoc(doc(db, "products", id));

      alert("Product deleted successfully!");
      navigate("/admin/products");
    } catch (error) {
      console.error("Error deleting product:", error);
      alert("Error deleting product. Check console for details.");
    } finally {
      setDeleting(false);
    }
  };

  // Helper function to calculate total stock
  const calculateTotalStock = (product) => {
    if (!product.PType || !Array.isArray(product.PType)) return 0;
    
    return product.PType.reduce((total, variant) => {
      if (!variant.PStock || !Array.isArray(variant.PStock)) {
        return total + (variant.PStock || 0);
      }
      
      const variantStock = variant.PStock.reduce((sum, stock) => {
        if (stock && typeof stock === 'object' && 'Qty' in stock) {
          return sum + (stock.Qty || 0);
        }
        return sum + (Number(stock) || 0);
      }, 0);
      
      return total + variantStock;
    }, 0);
  };

  // Helper function to get price range
  const getPriceRange = (product) => {
    if (!product.PType || !Array.isArray(product.PType) || product.PType.length === 0) {
      return { min: "N/A", max: "N/A", hasOffer: false };
    }

    const prices = product.PType
      .map(variant => variant.PPrice || 0)
      .filter(price => price > 0);
    
    if (prices.length === 0) {
      return { min: "N/A", max: "N/A", hasOffer: false };
    }

    const discountPrices = product.PType
      .filter(variant => variant.PInOffer && variant.PDiscountPrice > 0)
      .map(variant => variant.PDiscountPrice);

    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const minDiscountPrice = discountPrices.length > 0 ? Math.min(...discountPrices) : null;

    return {
      min: minPrice,
      max: maxPrice,
      minDiscount: minDiscountPrice,
      hasOffer: discountPrices.length > 0
    };
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-600 mb-4">Product Not Found</h2>
          <Link
            to="/admin/products"
            className="bg-purple-700 text-white px-4 py-2 rounded hover:bg-purple-800 transition"
          >
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  const totalStock = calculateTotalStock(product);
  const priceRange = getPriceRange(product);
  const hasAnyOffer = product.hasActiveOffer || false;
  const categoryHierarchy = getCategoryNamesWithHierarchy(); // NEW: Get organized categories

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        " "
      </div>
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">{product.PName}</h2>
          <p className="text-gray-600 mb-1">SKU: {product.PCode || "N/A"}</p>
          <p className="text-sm text-gray-500">
            Added: {product.PDateAdded ? new Date(product.PDateAdded).toLocaleDateString() : "N/A"}
          </p>
        </div>
        <div className="flex gap-3">
          <Link
            to={`/admin/products/${id}/edit`}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition font-medium"
          >
            Edit Product
          </Link>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition font-medium disabled:bg-red-400 disabled:cursor-not-allowed"
          >
            {deleting ? 'Deleting...' : 'Delete Product'}
          </button>
          <Link
            to="/admin/products"
            className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition font-medium"
          >
            Back to List
          </Link>
        </div>
      </div>

      {/* Short Description */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <h3 className="text-lg font-semibold mb-3 text-gray-800">Short Description</h3>
        <p className="text-gray-700 leading-relaxed">{product.PDesc || "No short description available."}</p>
      </div>

      {/* NEW: Detailed Description */}
      {product.PDetailedDesc && (
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h3 className="text-lg font-semibold mb-3 text-gray-800">Detailed Description</h3>
          <MarkdownPreview content={product.PDetailedDesc} />
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          {['overview', 'categories', 'variants', 'media', 'stock'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <h4 className="text-sm font-medium text-gray-500 mb-2">Price Range</h4>
            <div className="text-lg font-semibold text-gray-900">
              {priceRange.min === "N/A" ? (
                "No prices"
              ) : priceRange.min === priceRange.max ? (
                <div>
                  {priceRange.hasOffer ? (
                    <>
                      <span className="text-sm line-through text-gray-500 block">PKR{priceRange.min.toFixed(2)}</span>
                      <span className="text-red-600">PKR{priceRange.minDiscount.toFixed(2)}</span>
                    </>
                  ) : (
                    <span>PKR{priceRange.min.toFixed(2)}</span>
                  )}
                </div>
              ) : (
                <div>
                  {priceRange.hasOffer ? (
                    <>
                      <span className="text-sm line-through text-gray-500 block">PKR{priceRange.min.toFixed(2)} - PKR{priceRange.max.toFixed(2)}</span>
                      <span className="text-red-600">PKR{priceRange.minDiscount.toFixed(2)} - PKR{priceRange.max.toFixed(2)}</span>
                    </>
                  ) : (
                    <span>PKR{priceRange.min.toFixed(2)} - PKR{priceRange.max.toFixed(2)}</span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-4">
            <h4 className="text-sm font-medium text-gray-500 mb-2">Total Stock</h4>
            <div className={`text-lg font-semibold ${
              totalStock > 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {totalStock} units
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-4">
            <h4 className="text-sm font-medium text-gray-500 mb-2">Variants</h4>
            <div className="text-lg font-semibold text-gray-900">
              {product.PType?.length || 0} variants
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-4">
            <h4 className="text-sm font-medium text-gray-500 mb-2">Categories</h4>
            <div className="text-lg font-semibold text-gray-900">
              {categoryHierarchy.length} categor{categoryHierarchy.length !== 1 ? 'ies' : 'y'}
            </div>
          </div>

          {/* UPDATED: Offer Status Card using hasActiveOffer field */}
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <h4 className="text-sm font-medium text-gray-500 mb-2">Offer Status</h4>
            <div className="flex flex-col gap-2">
              <span className={`px-3 py-1 text-sm rounded-full font-medium ${
                hasAnyOffer
                  ? "bg-green-100 text-green-800 border border-green-200"
                  : "bg-gray-100 text-gray-800 border border-gray-200"
              }`}>
                {hasAnyOffer ? "Active Offers" : "Standard"}
              </span>
              {/* Additional info about variants with offers */}
              {hasAnyOffer && (
                <div className="text-xs text-gray-600">
                  {product.PType?.filter(variant => variant.PInOffer).length || 0} variant(s) on offer
                </div>
              )}
            </div>
          </div>

          {/* NEW: Detailed Description Status */}
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <h4 className="text-sm font-medium text-gray-500 mb-2">Detailed Description</h4>
            <div className="flex flex-col gap-2">
              <span className={`px-3 py-1 text-sm rounded-full font-medium ${
                product.PDetailedDesc
                  ? "bg-blue-100 text-blue-800 border border-blue-200"
                  : "bg-gray-100 text-gray-800 border border-gray-200"
              }`}>
                {product.PDetailedDesc ? "Available" : "Not Available"}
              </span>
              {product.PDetailedDesc && (
                <div className="text-xs text-gray-600">
                  {product.PDetailedDesc.length} characters
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden mb-6">
          <div className="px-6 py-4 border-b bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-800">Product Categories</h3>
            <p className="text-sm text-gray-600 mt-1">
              {categoryHierarchy.length} categor{categoryHierarchy.length !== 1 ? 'ies' : 'y'} assigned to this product
            </p>
          </div>
          <div className="p-6">
            {categoryHierarchy.length > 0 ? (
              <div className="space-y-3">
                {organizedCategories.map(mainCat => {
                  const isMainSelected = product.PCategories.includes(mainCat.id);
                  const selectedSubcategories = mainCat.subcategories?.filter(sub => 
                    product.PCategories.includes(sub.id)
                  ) || [];

                  if (!isMainSelected && selectedSubcategories.length === 0) {
                    return null;
                  }

                  return (
                    <div key={mainCat.id} className="border rounded-lg p-4 bg-gray-50">
                      {/* Main Category */}
                      {isMainSelected && (
                        <div className="flex items-center mb-2">
                          <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                          <span className="font-semibold text-gray-900">{mainCat.CategoryName}</span>
                          <span className="ml-2 text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                            Main Category
                          </span>
                        </div>
                      )}
                      
                      {/* Subcategories */}
                      {selectedSubcategories.length > 0 && (
                        <div className={`${isMainSelected ? 'ml-5 border-l-2 border-purple-200 pl-4' : ''}`}>
                          {selectedSubcategories.map(subCat => (
                            <div key={subCat.id} className="flex items-center mt-2">
                              <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                              <span className="text-gray-700">{subCat.CategoryName}</span>
                              <span className="ml-2 text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                                Subcategory
                              </span>
                              {!isMainSelected && (
                                <span className="ml-2 text-xs text-gray-500">
                                  (under {mainCat.CategoryName})
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <p className="text-gray-500">No categories assigned to this product.</p>
              </div>
            )}
            
            {/* Category Summary */}
            {categoryHierarchy.length > 0 && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-800 mb-2">Category Summary</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-blue-700">Total Categories:</span>
                    <span className="ml-2 text-blue-900">{categoryHierarchy.length}</span>
                  </div>
                  <div>
                    <span className="font-medium text-blue-700">Main Categories:</span>
                    <span className="ml-2 text-blue-900">
                      {categoryHierarchy.filter(cat => cat.type === 'main').length}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-blue-700">Subcategories:</span>
                    <span className="ml-2 text-blue-900">
                      {categoryHierarchy.filter(cat => cat.type === 'sub').length}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Variants Tab */}
      {activeTab === 'variants' && (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden mb-6">
          <div className="px-6 py-4 border-b bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-800">Product Variants</h3>
            <div className="flex items-center gap-2 mt-2">
              <span className={`w-2 h-2 rounded-full ${hasAnyOffer ? 'bg-green-500' : 'bg-gray-400'}`}></span>
              <span className="text-sm text-gray-600">
                Product {hasAnyOffer ? 'has active offers' : 'has no active offers'}
              </span>
            </div>
          </div>
          <div className="divide-y">
            {product.PType?.map((variant, index) => (
              <div key={variant.id || index} className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h4 className="text-xl font-semibold text-gray-900">{variant.PTypeName}</h4>
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 text-sm rounded-full ${
                      variant.PInOffer 
                        ? 'bg-green-100 text-green-800 border border-green-200' 
                        : 'bg-gray-100 text-gray-800 border border-gray-200'
                    }`}>
                      {variant.PInOffer ? 'On Offer' : 'Standard'}
                    </span>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Price</label>
                    <p className="text-lg font-semibold">
                      {variant.PInOffer && variant.PDiscountPrice ? (
                        <>
                          <span className="line-through text-gray-500 mr-2">PKR{variant.PPrice?.toFixed(2)}</span>
                          <span className="text-red-600">PKR{variant.PDiscountPrice?.toFixed(2)}</span>
                        </>
                      ) : (
                        <span>PKR{variant.PPrice?.toFixed(2)}</span>
                      )}
                    </p>
                    {variant.PInOffer && variant.PDiscountPrice && (
                      <p className="text-xs text-green-600 mt-1">
                        Save PKR{(variant.PPrice - variant.PDiscountPrice).toFixed(2)} 
                        ({(100 - (variant.PDiscountPrice / variant.PPrice) * 100).toFixed(1)}% off)
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Sole Color</label>
                    <p className="text-lg">{variant.PSoleColor || "N/A"}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Variant Stock</label>
                    <p className="text-lg font-semibold">
                      {variant.PStock?.reduce((sum, stock) => sum + (stock.Qty || 0), 0) || 0} units
                    </p>
                  </div>
                </div>

                {/* Stock Details */}
                {variant.PStock && Array.isArray(variant.PStock) && variant.PStock.length > 0 && (
                  <div className="mt-4">
                    <label className="text-sm font-medium text-gray-500 mb-2 block">Stock by Size</label>
                    <div className="flex flex-wrap gap-2">
                      {variant.PStock.map((stock, stockIndex) => (
                        <span key={stockIndex} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                          {stock.Size}: {stock.Qty} units
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Media Tab */}
      {activeTab === 'media' && (
        <div className="space-y-6">
          {/* Images */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">Product Images</h3>
            {product.PType?.some(variant => variant.PImgUrls?.length > 0) ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {product.PType?.map((variant, variantIndex) => (
                  <div key={variantIndex}>
                    <h4 className="text-sm font-medium text-gray-600 mb-2">{variant.PTypeName}</h4>
                    <div className="flex flex-wrap gap-2">
                      {variant.PImgUrls?.map((url, i) => (
                        <img
                          key={i}
                          src={url}
                          alt={`${variant.PTypeName} - ${i + 1}`}
                          className="w-20 h-20 object-cover border rounded hover:scale-105 transition-transform cursor-pointer"
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No images available.</p>
            )}
          </div>

          {/* Videos */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">Product Videos</h3>
            {product.PType?.some(variant => variant.PVidUrls?.length > 0) ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {product.PType?.map((variant, variantIndex) => (
                  <div key={variantIndex}>
                    <h4 className="text-sm font-medium text-gray-600 mb-2">{variant.PTypeName}</h4>
                    <div className="flex flex-wrap gap-4">
                      {variant.PVidUrls?.map((url, i) => (
                        <div key={i} className="flex flex-col">
                          <video 
                            src={url} 
                            controls 
                            className="w-48 h-32 rounded border object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No videos available.</p>
            )}
          </div>
        </div>
      )}

      {/* Stock Tab */}
      {activeTab === 'stock' && (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-6 py-4 border-b bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-800">Stock Management</h3>
            <div className="flex items-center gap-4 mt-2 text-sm">
              <span className="text-gray-600">Total Stock: <strong>{totalStock} units</strong></span>
              <span className="text-gray-600">Variants: <strong>{product.PType?.length || 0}</strong></span>
              <span className={`px-2 py-1 rounded-full text-xs ${
                hasAnyOffer ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {hasAnyOffer ? 'Active Offers' : 'No Offers'}
              </span>
            </div>
          </div>
          <div className="divide-y">
            {product.PType?.map((variant, index) => {
              const variantStock = variant.PStock?.reduce((sum, stock) => sum + (stock.Qty || 0), 0) || 0;
              return (
                <div key={variant.id || index} className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h4 className="text-xl font-semibold text-gray-900">{variant.PTypeName}</h4>
                      {variant.PInOffer && (
                        <span className="text-sm text-green-600 font-medium">On Offer</span>
                      )}
                    </div>
                    <span className={`px-3 py-1 text-sm rounded-full ${
                      variantStock > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {variantStock} units total
                    </span>
                  </div>
                  
                  {variant.PStock && Array.isArray(variant.PStock) && variant.PStock.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {variant.PStock.map((stock, stockIndex) => (
                        <div key={stockIndex} className="border rounded-lg p-4 bg-gray-50">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium text-gray-900">{stock.Size || "No Size"}</span>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              stock.Qty > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {stock.Qty || 0} units
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">No stock information available.</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}